import { Component, OnInit } from '@angular/core';
import { AlertsService, ConsentsService, NamedComponent, ViewHistoryService } from '@backoffice-monorepo/commons';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import {
  BrandAssignmentPair,
  ConsentBrandAssignment, ConsentChannelDetails,
  ConsentDefinitionResponse,
  ConsentRegistrationType,
  CreateConsentDefinitionRequest,
  RegistrationStepsPair,
  UpdateConsentDefinitionRequest
} from '@twino/backoffice-api';
import { ActivatedRoute } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import { ConsentsRefreshService } from '../../services/consents-refresh.service';

@Component({
  selector: 'backoffice-monorepo-consent-definition',
  templateUrl: './consent-definition.component.html',
  styleUrls: ['./consent-definition.component.scss']
})
export class ConsentDefinitionComponent extends NamedComponent implements OnInit {

  id: number;
  consentForm: FormGroup;
  control: FormArray;
  tabTitle: string;
  consent: ConsentDefinitionResponse | undefined;
  consentName: string | null;
  productBrandsList$: Observable<string[]>;
  consentRegistrationType = ConsentRegistrationType;
  consentRegistrationTypeOptions = Object.keys(this.consentRegistrationType);
  selectedTypes: string[] = [];
  brandAssignmentData: Record<string, ConsentBrandAssignment> | never;
  isEdit = false;

  constructor(
    activatedRoute: ActivatedRoute,
    private alertService: AlertsService,
    private formBuilder: FormBuilder,
    private viewHistoryService: ViewHistoryService,
    private consentConfigService: ConsentsService,
    private consentRefreshService: ConsentsRefreshService,
  ) {
    super(activatedRoute);
  }

  ngOnInit(): void {
    this.id = this.activatedRoute.snapshot.params['id'];
    this.tabTitle = (this.id > 0) ? `Update #${this.id}` : `Create`;
    this.isEdit = (this.id > 0);
    this.consentForm = this.formBuilder.group({
      name: ['', Validators.required],
      mandatory: [false],
      defaultText: ['', Validators.required],
      canClientEdit: [false],
      registrationSteps: this.formBuilder.array([]),
      brandAssignment: this.formBuilder.array([]),
      parentName: ['']
    });
    if (this.id > 0) {
      this.getConsent();
    } else {
      this.consentForm.addControl('key', new FormControl('', Validators.required));
    }
  }

  getName(): string {
    return `${this.tabTitle} consent definition`;
  }

  createForm(consentRegistrationType: string): FormGroup {
    return this.formBuilder.group({
      consentRegistrationType: [consentRegistrationType, Validators.required],
      amount: ['', Validators.required],
    });
  }

  get getFormControls() {
    return this.consentForm.get('registrationSteps') as FormArray;
  }

  setRows(consent: ConsentDefinitionResponse) {
    const formControl1 = this.consentForm.get('registrationSteps') as FormArray;
    if (consent.registrationSteps) {
      Object.entries(consent.registrationSteps).forEach(item => {
        const formGroup = this.createForm(item[0]);
        this.disableType(item[0]);
        formGroup.setValue({
          consentRegistrationType: item[0],
          amount: item[1],
        });
        formControl1.push(formGroup);
      });
    }
  }

  addRow(consentRegistrationType: string) {
    if(consentRegistrationType !== "0") {
      const control  = this.consentForm.get('registrationSteps') as FormArray;
      control.push(this.createForm(consentRegistrationType));
      this.disableType(consentRegistrationType);
    }
  }

  deleteRow(index: number) {
    const control  = this.consentForm.get('registrationSteps') as FormArray;
    this.enableType(control.controls[index].value.consentRegistrationType);
    control.removeAt(index);
  }

  getConsent() {
   this.consentConfigService.getConsentDefinitionById(this.id).pipe(take(1)).subscribe(response => {
      this.consent = response;
      this.consentName = this.consent.name;
      const consentData = {
        name: this.consent.name,
        mandatory: this.consent.mandatory,
        defaultText: this.consent.defaultText,
        canClientEdit: this.consent.canClientEdit,
        parentName: this.consent.parentName,
        registrationSteps: [],
        brandAssignment: [],
      }
      this.consentForm.setValue(consentData);
      this.brandAssignmentData = this.consent.brandAssignments;
      this.setRows(this.consent);
    })
  }

  validateForm() {
    return !this.consentForm.valid;
  }

  submitForm() {
    const registrationSteps: RegistrationStepsPair[] = this.consentForm.value.registrationSteps;
    const registrationStepsMap: Record<ConsentRegistrationType, number> = {} as Record<ConsentRegistrationType, number>;
    registrationSteps.forEach((item: RegistrationStepsPair) => {
      registrationStepsMap[item.consentRegistrationType] = item.amount;
    });

    const brandAssignments = this.consentForm.value.brandAssignment;
    const brandAssignmentsMap: Record<string, ConsentBrandAssignment> = {} as Record<string, ConsentBrandAssignment>;
    brandAssignments.forEach((item: BrandAssignmentPair) => {
      const channelDetailsMap: Record<string, ConsentChannelDetails> = {};
      item.channelDetails.forEach(detail => {
        channelDetailsMap[detail.identifier] = {text: detail.text, collect: detail.collect}
      })
      brandAssignmentsMap[item.brand] = {
        channelDetails: channelDetailsMap,
      }
    });

    if (this.id > 0) {
      const consentConfigRequest: UpdateConsentDefinitionRequest = {
        canClientEdit: this.consentForm.value.canClientEdit,
        defaultText: this.consentForm.value.defaultText,
        mandatory: this.consentForm.value.mandatory,
        parentName: this.consentForm.value.parentName,
        registrationSteps: registrationStepsMap,
        brandAssignment: brandAssignmentsMap,
      }
      this.updateConsent(consentConfigRequest);
    } else {
      const consentConfigRequest: CreateConsentDefinitionRequest = {
        key: this.consentForm.value.key,
        name: this.consentForm.value.name,
        canClientEdit: this.consentForm.value.canClientEdit,
        defaultText: this.consentForm.value.defaultText,
        mandatory: this.consentForm.value.mandatory,
        parentName: this.consentForm.value.parentName,
        registrationSteps: registrationStepsMap,
        brandAssignment: brandAssignmentsMap,
      }
      this.addConsent(consentConfigRequest);
    }
  }

  addConsent(consentConfigRequest: CreateConsentDefinitionRequest) {
    this.consentConfigService.createConsentDefinition(consentConfigRequest).pipe(
      takeUntil(this.$destroy)
    )
      .subscribe(() => {
        this.alertService.notifySuccess(`Consent configuration ${this.consentForm.value.name} added`);
        this.viewHistoryService.closeTab();
        this.consentRefreshService.reloadConsentModel();
      })
  }

  updateConsent(consentConfigRequest: UpdateConsentDefinitionRequest) {
    this.consentConfigService.updateConsentDefinition(
      this.id,
      consentConfigRequest).pipe(
      takeUntil(this.$destroy)
    )
      .subscribe(() => {
        this.alertService.notifySuccess(`Consent configuration ${this.consent.name} updated`);
        this.viewHistoryService.closeTab();
        this.consentRefreshService.reloadConsentModel();
      })
  }

  disableType(consentRegistrationType: string) {
    this.selectedTypes.push(consentRegistrationType);
  }

  enableType(consentRegistrationType: string) {
    this.selectedTypes.forEach((value, index)=>{
      if(value === consentRegistrationType) this.selectedTypes.splice(index, 1);
    });
  }

  isDisabledType(consentRegistrationType: string): boolean {
    return this.selectedTypes.includes(consentRegistrationType);
  }

  trackBy(_: number, control: AbstractControl): AbstractControl {
    return control;
  }
}
